// The canvases of traces a reader opens over the page. Each is one board: the
// trace it is anchored on, the sessions laid on beside it, what the reader did
// there (readings, the nodes they closed, card sizes, the order its cards
// stack in), the chat it is worked in, and whether it lays on the sessions
// that chat looks into (it does, except the ones the reader took off). The
// notes a chat pins on a board are the chat's, kept in its transcript
// (lib/chatWorkspaceNotes.js), not the board's. A trace's own canvas is kept
// by that trace (`trace:<session>`); one opened from a chat's investigation
// by that chat's graph (`chat:<graph key>`); one the reader started in the
// workspace home by its own id (`pad:`), with the machines and findings it
// holds beside its sessions; one Investigate hands a case over in by the
// case (`case:<scope>:<case>`), and one it lays several cases out on together
// by those cases (`cases:<scope>:<key>`). Kept in this browser for the
// canvases opened most recently. Which canvas is up, and what is selected on
// it, are this tab's only: a reload comes back to the page under it.
import { useSyncExternalStore } from "react";
import { deckShown } from "./deckRequests.js";
import { askWorkspaceMessage, cleanBoard, cleanOrder, cleanSizes, cleanTraces, workspaceCanvasSnapshot } from "./workspaceStore.js";

const KEY = "origin-trace-canvas-v1";
// Canvases kept, the most recently opened.
const KEPT = 30;
// Sessions laid on beside the anchor: it and these are a board's cards.
const MAX_LAYERED = 39;
export const MAX_BOARD_SESSIONS = MAX_LAYERED + 1;
// Sessions taken off that following the chat leaves off.
const MAX_DISMISSED = 200;
// What a workspace holds besides its sessions: machines, each a card of its
// sessions on the board ({ id, name }); findings pinned open on their
// sessions' cards ({ id, session, title }); the reader's own notes, loose on
// the board ({ id, text, at }); and searches to branch out from, each a card
// of the sessions it matches ({ id, q, label }: a file path, a topic, any
// query the session search reads). A workspace may hold no session at all.
const MAX_MACHINES = 12;
const MAX_FINDINGS = 40;
const MAX_LOOSE_NOTES = 30;
const MAX_SEARCHES = 12;
// Other workspaces inside this one ({ id }): a card each, opened by going in.
const MAX_NESTED = 12;

const lower = (id) => String(id || "").toLowerCase();
// The first of each id, the newest `max` kept.
const firstOf = (list, max) => {
  const seen = new Set();
  return list.filter((x) => !seen.has(x.id) && (seen.add(x.id), true)).slice(-max);
};
const cleanMachines = (raw) => firstOf((Array.isArray(raw) ? raw : []).filter((m) => typeof m?.id === "string" && m.id).map((m) => ({ id: m.id, name: String(m.name || "") })), MAX_MACHINES);
const cleanFindings = (raw) => firstOf((Array.isArray(raw) ? raw : []).filter((f) => f?.id != null && f?.session).map((f) => ({ id: String(f.id), session: lower(f.session), title: String(f.title || "") })), MAX_FINDINGS);
// A note's place on its board (see notePlace in SessionCanvasView): where it
// was put (`x`, `y`), and the moment under its left edge (`t`) with how far
// past either end of the clock it sits (`dx`, in the board's units).
const notePlaceOf = (n) => ({
  ...(Number.isFinite(n?.x) ? { x: Math.round(n.x) } : {}), ...(Number.isFinite(n?.y) ? { y: Math.round(n.y) } : {}),
  ...(Number.isFinite(n?.t) ? { t: Math.round(n.t) } : {}), ...(Number.isFinite(n?.dx) && Math.round(n.dx) !== 0 ? { dx: Math.round(n.dx) } : {}),
});
// The cells a note is pinned on ({ id, session, turn, lane }: the turn as
// its record prints it, from the record's start, and the lane's id), a pin
// on each tied to the note by a line. One cell once; the newest kept.
const MAX_NOTE_LINKS = 12;
export function cleanNoteLinks(raw) {
  const seen = new Set();
  return (Array.isArray(raw) ? raw : [])
    .filter((l) => typeof l?.id === "string" && l.id && l.session && Number.isInteger(l.turn) && l.turn >= 0)
    .map((l) => ({ id: l.id, session: lower(l.session), turn: l.turn, ...(l.lane ? { lane: String(l.lane) } : {}) }))
    .filter((l) => {
      const at = `${l.session} ${l.turn} ${l.lane || ""}`;
      if (seen.has(at) || seen.has(l.id)) return false;
      seen.add(at);
      seen.add(l.id);
      return true;
    })
    .slice(-MAX_NOTE_LINKS);
}
const cleanLooseNotes = (raw) => firstOf((Array.isArray(raw) ? raw : []).filter((n) => typeof n?.id === "string" && n.id).map((n) => {
  const links = cleanNoteLinks(n.links);
  return { id: n.id, text: String(n.text || "").slice(0, 4000), at: Number(n.at) || 0, ...notePlaceOf(n), ...(links.length ? { links } : {}) };
}), MAX_LOOSE_NOTES);
const cleanSearches = (raw) => firstOf((Array.isArray(raw) ? raw : []).filter((q) => typeof q?.id === "string" && q.id && typeof q.q === "string" && q.q.trim()).map((q) => ({ id: q.id, q: q.q.trim().slice(0, 500), label: String(q.label || "") })), MAX_SEARCHES);
const cleanNested = (raw) => firstOf((Array.isArray(raw) ? raw : []).filter((w) => typeof w?.id === "string" && w.id).map((w) => ({ id: w.id })), MAX_NESTED);
const holdsAnything = (c) => !!(c.anchor || c.layered.length || c.machines.length || c.findings.length || c.looseNotes.length || c.searches.length || c.nested?.length);
const thingId = (prefix) => `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const cleanIds = (raw) => (Array.isArray(raw) ? raw : []).filter((id) => typeof id === "string" && id).map(lower).slice(-MAX_DISMISSED);
// The cases a `cases:` board was laid out for, by id: a session at least each.
const cleanCases = (raw) => [...new Set((Array.isArray(raw) ? raw : []).filter((id) => typeof id === "string" && id))].slice(0, MAX_BOARD_SESSIONS);
export const traceCanvasId = (sessionId) => (lower(sessionId) ? `trace:${lower(sessionId)}` : null);
export const chatCanvasId = (graphKey) => (graphKey ? `chat:${graphKey}` : null);

// A canvas's order holds the session graph's own cards too (open there, not
// laid on), so it keeps any session's place, not only the ones laid on.
const cleanCanvasOrder = (raw) => cleanOrder(raw, (Array.isArray(raw) ? raw : []).filter((x) => typeof x === "string" && x).map((x) => ({ id: x.toLowerCase() })));

function cleanRecord(raw) {
  const anchor = lower(raw?.anchor);
  const layered = cleanTraces(raw?.layered).filter((t) => t.id !== anchor).slice(-MAX_LAYERED);
  // Notes a canvas kept before they were its chat's are left behind.
  const { notes: _notes, ...board } = cleanBoard(raw, [{ id: anchor }, ...layered]);
  return {
    anchor,
    title: typeof raw?.title === "string" ? raw.title : "",
    // Named by the reader, it keeps that name over its trace's.
    named: raw?.named === true,
    layered,
    ...board,
    order: cleanCanvasOrder(raw?.order),
    machines: cleanMachines(raw?.machines),
    findings: cleanFindings(raw?.findings),
    looseNotes: cleanLooseNotes(raw?.looseNotes),
    searches: cleanSearches(raw?.searches),
    nested: cleanNested(raw?.nested),
    cases: cleanCases(raw?.cases),
    chatId: raw?.chatId != null ? String(raw.chatId) : null,
    // Its chat was begun in it, first asked there (a trace's or the
    // reader's own workspace); one opened from a chat's investigation is
    // that chat's.
    chatAsked: raw?.chatAsked === true,
    // The reader's (something put on it, or its chat asked): listed with the
    // workspaces however little it holds, and kept until deleted.
    saved: raw?.saved === true,
    follow: raw?.follow !== false,
    dismissed: cleanIds(raw?.dismissed),
    at: Number(raw?.at) || 0,
  };
}

function load() {
  try {
    const raw = JSON.parse(globalThis.localStorage?.getItem(KEY) || "null");
    const out = {};
    for (const [id, rec] of Object.entries(raw && typeof raw === "object" ? raw : {})) {
      // A trace's canvas from before canvases had ids: keyed by the trace.
      const key = id.includes(":") ? id : traceCanvasId(id);
      const clean = cleanRecord(id.includes(":") ? rec : { ...rec, anchor: rec?.anchor || id });
      // A workspace once kept whatever chat was in front as it came up,
      // empty, which may have been asked anywhere since: it lets that one go.
      if (key && !key.startsWith("chat:") && !clean.chatAsked) clean.chatId = null;
      if (key && holdsAnything(clean)) out[key] = clean;
    }
    return out;
  } catch {
    return {};
  }
}

let records = load();
let open = null;
let selection = null;
// Under a preview (previewTraceCanvas), the canvas the pane worked in shows
// and what was selected on it, put back when the preview goes.
let under = null;
let underSelection = null;
const subscribers = new Set();

function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function emit() {
  for (const fn of subscribers) fn();
}

function commit(next) {
  records = next;
  try { globalThis.localStorage?.setItem(KEY, JSON.stringify(records)); } catch { /* private window: this tab only */ }
  emit();
}

// One canvas, changed by `fn` (which returns its new fields).
function update(id, fn) {
  const cur = id ? records[id] : null;
  if (!cur) return;
  const patch = fn(cur);
  if (!patch) return;
  commit({ ...records, [id]: { ...cur, ...patch } });
}

// Keeps a canvas (bumped to the most recent); the most recently used few
// are kept, and every one the reader saved.
function keep(id, rec) {
  const next = { ...records, [id]: { ...rec, at: Date.now() } };
  for (const old of Object.keys(next).filter((k) => !next[k].saved).sort((a, b) => next[b].at - next[a].at).slice(KEPT)) delete next[old];
  return next;
}

// Lays a canvas open over the page, in place of any preview. Opened from a
// preview's own chat (an investigation it links to), it goes to the pane the
// workspace home works in, in place of what the pane showed, so nothing waits
// under a preview to come back.
function show(id, rec, { title = "", trace = null, focus = null } = {}) {
  const next = keep(id, rec);
  open = { id, title: String((rec.named ? rec.title : title) || rec.title || ""), trace: trace || null, ...(focus ? { focus: lower(focus) } : {}) };
  selection = null;
  under = null;
  underSelection = null;
  commit(next);
  return id;
}

const freshLayered = (rec, entries, { skip = [] } = {}) => {
  const have = new Set([rec.anchor, ...rec.layered.map((t) => t.id), ...skip]);
  const fresh = (Array.isArray(entries) ? entries : [entries])
    .map((e) => (typeof e === "string" ? { id: e } : e))
    .filter((e) => e?.id)
    .map((e) => ({ id: lower(e.id), title: String(e.title || ""), addedAt: Date.now() }))
    .filter((e) => !have.has(e.id) && (have.add(e.id), true));
  return fresh.length ? [...rec.layered, ...fresh].slice(-MAX_LAYERED) : null;
};

// A trace's own canvas, over the page: as it was left, or holding the trace
// alone. `trace` is the session's record when the opener already holds it
// (the trace page has read it), for the canvas to show without reading it
// again; it is this tab's only, never kept in storage.
export function openTraceCanvas({ id, title = "", trace = null } = {}) {
  const key = traceCanvasId(id);
  if (!key) return null;
  return show(key, records[key] || cleanRecord({ anchor: id, title }), { title, trace });
}

// A trace's canvas looked at before it is kept. With the workspace home's
// panes on screen it comes up over them (`preview`), a workspace like any
// other while it is up: the one open, which the chat's workspace tools act
// on. What the pane worked in shows waits under it (`under`) and is the open
// one again when the preview goes, so a click on the wrong session is undone
// by closing it; the preview's Mount opens it in that pane
// (mountTracePreview). Anywhere else it is laid over the page
// (openTraceCanvas), which closes the same way. `message`: the message to open
// on (a finding's), picked and read on the preview's board.
export function previewTraceCanvas({ id, title = "", trace = null, message = null } = {}) {
  if (!deckShown()) {
    const key = openTraceCanvas({ id, title, trace });
    if (key && message) askWorkspaceMessage(id, message);
    return key;
  }
  const key = traceCanvasId(id);
  if (!key) return null;
  const rec = records[key] || cleanRecord({ anchor: id, title });
  if (!open?.preview) {
    under = open?.inDeck ? open : null;
    underSelection = under ? selection : null;
  }
  open = {
    id: key, title: String((rec.named ? rec.title : title) || rec.title || ""), trace: trace || null, preview: true,
    ...(message ? { focus: `${lower(id)}#${lower(message)}` } : {}),
  };
  selection = null;
  commit(keep(key, rec));
  return key;
}

// The preview goes; what the pane worked in shows is the open canvas again.
export function closeTracePreview() {
  if (!open?.preview) return;
  open = under;
  selection = underSelection;
  under = null;
  underSelection = null;
  emit();
}

// The canvas previewed over the workspace home's panes ({ id, title, trace,
// preview, focus? }), or null.
export function tracePreview() {
  return open?.preview ? open : null;
}

export function useTracePreview() {
  return useSyncExternalStore(subscribe, tracePreview, tracePreview);
}

// A workspace kept in the sidebar's list until it is deleted, however little
// it holds: the reader's, once something is put on it or its chat is asked.
export function saveTraceCanvas(id) {
  update(id, (c) => (c.saved ? null : { saved: true }));
}

// The preview's Mount: its workspace open in the preview's place as one
// opened anywhere else is, which the workspace home shows in the pane it
// works in (in place of what that showed, which stays kept), on the message
// the preview was on. There the sidebar's sessions and workspaces can be
// dragged onto it. It is not saved by being mounted: a look leaves nothing in
// the sidebar's list until something is put on it or its chat is asked.
// Nothing waits under it to come back.
export function mountTracePreview() {
  if (!open?.preview || !records[open.id]) return null;
  const { id, title, trace, focus } = open;
  open = { id, title, trace, ...(focus ? { focus } : {}) };
  under = null;
  underSelection = null;
  commit(keep(id, records[id]));
  return id;
}

// A trace's own canvas, kept (as it was left, or holding the trace alone)
// without laying it over the page: the trace page's Lanes reading draws that
// same board in place of the trace's lanes (its session graph), and drawing it
// there counts as using it. It is the canvas the trace's workspace opens on.
export function keepTraceCanvas({ id, title = "" } = {}) {
  const key = traceCanvasId(id);
  if (!key) return null;
  commit(keep(key, records[key] || cleanRecord({ anchor: id, title })));
  return key;
}

// The canvas of a chat's investigation, over the page: anchored on the trace
// most of its claims rest on, the others laid on beside it (and any it has
// found since the last time, except the ones the reader took off), worked in
// that chat. `traces` come strongest first; `focus` is one of them to bring
// into view (a claim's).
export function openChatCanvas({ graphKey, title = "", chatId = null, traces = [], focus = null } = {}) {
  const key = chatCanvasId(graphKey);
  const list = (traces || []).filter((t) => t?.id);
  if (!key || (!records[key] && !list.length)) return null;
  const base = records[key] || cleanRecord({ anchor: list[0].id, title });
  const layered = freshLayered(base, list, { skip: base.dismissed }) || base.layered;
  return show(key, { ...base, layered, title: title || base.title, chatId: chatId != null ? String(chatId) : base.chatId }, { title: title || base.title, focus });
}

// A case Investigate hands over (`case:<scope>:<case>`), as a workspace over
// the page: anchored on `anchor` (the session of its most pressing finding),
// so it is a trace's workspace like any other, its dock and all, with the
// case's other sessions laid on beside it, its findings pinned open on their
// cards, and Investigate's account of it as a note at the top. Opened again,
// it is as it was left, with the sessions the case has gained since laid on
// (except ones the reader took off) and the account written again; one kept
// from before cases had an anchor takes it then. `focus` is the session to
// bring into view, or `<session>#<message>` to open on that moment; `open`
// false keeps it without laying it over the page.
//
// Several cases laid out together (`cases:<scope>:<key>`, the key a hash of
// their ids, so the same cases open the same board again) are kept the same
// way, with the ids of the cases they were opened for (`cases`).
export const caseCanvasId = (scopeKey, caseId) => (caseId ? `case:${scopeKey || "org"}:${caseId}` : null);
export function casesCanvasId(scopeKey, caseIds) {
  const ids = [...new Set((caseIds || []).map((id) => String(id || "")).filter(Boolean))].sort();
  if (!ids.length) return null;
  // FNV-1a over the sorted ids.
  let h = 0x811c9dc5;
  for (const ch of ids.join("\n")) h = Math.imul(h ^ ch.codePointAt(0), 0x01000193) >>> 0;
  return `cases:${scopeKey || "org"}:${h.toString(16).padStart(8, "0")}`;
}
export function openCaseCanvas({ id, title = "", anchor = "", sessions = [], findings = [], account = null, cases = [], focus = null, open: lay = true } = {}) {
  if (!id || !(id.startsWith("case:") || id.startsWith("cases:"))) return null;
  const lead = lower(anchor);
  const had = records[id];
  const base = !had ? cleanRecord({ anchor: lead, title })
    : had.anchor || !lead ? had
      : { ...had, anchor: lead, layered: had.layered.filter((t) => t.id !== lead) };
  const layered = freshLayered(base, sessions, { skip: base.dismissed }) || base.layered;
  let looseNotes = base.looseNotes;
  if (account?.id && account.text) {
    looseNotes = looseNotes.some((n) => n.id === account.id)
      ? looseNotes.map((n) => (n.id === account.id ? { ...n, text: String(account.text).slice(0, 4000) } : n))
      : cleanLooseNotes([{ id: account.id, text: account.text, at: Date.now() }, ...looseNotes]);
  }
  const rec = {
    ...base, layered, findings: cleanFindings([...base.findings, ...findings]), looseNotes,
    cases: cases.length ? cleanCases(cases) : base.cases,
    title: base.named ? base.title : title || base.title,
  };
  if (!holdsAnything(rec)) return null;
  if (!lay) {
    commit(keep(id, rec));
    return id;
  }
  return show(id, rec, { title: rec.title, focus });
}

// The kept boards several cases were laid out on together that hold `caseId`.
export function casesCanvasesWith(scopeKey, caseId) {
  const head = `cases:${scopeKey || "org"}:`;
  return caseId ? Object.keys(records).filter((id) => id.startsWith(head) && (records[id].cases || []).includes(caseId)) : [];
}

// A kept canvas, opened again as it was left: a workspace the reader saved
// by working in it. False when it is no longer kept.
export function reopenTraceCanvas(id) {
  const rec = id ? records[id] : null;
  if (!rec) return false;
  show(id, rec, { title: rec.title });
  return true;
}

// The canvases kept in this browser, the most recently used first: what the
// workspace home lists as saved workspaces, each with what it holds.
let listed = null;
// What a workspace is called where it is listed: its title, else its first
// note's first line, else its first search; none for one with none of them.
const listedTitle = (c) => c.title || (c.looseNotes[0]?.text || "").split("\n")[0].slice(0, 80) || c.searches[0]?.label || "";

function listCanvases() {
  if (listed?.records === records) return listed.list;
  const list = Object.entries(records)
    .map(([id, c]) => ({
      id, title: listedTitle(c), anchor: c.anchor,
      count: (c.anchor ? 1 : 0) + c.layered.length, machines: c.machines.length, nested: c.nested.length, notes: c.looseNotes.length, searches: c.searches.length, saved: c.saved,
      at: c.at, chat: id.startsWith("chat:"),
    }))
    .sort((a, b) => b.at - a.at);
  listed = { records, list };
  return list;
}

// What a workspace holds, said in a line.
export function holdsText(w) {
  const n = (k, one, many = `${one}s`) => (k ? `${k} ${k === 1 ? one : many}` : null);
  return [n(w.count, "session"), n(w.notes, "note"), n(w.searches, "search", "searches"), n(w.machines, "machine"), n(w.nested, "workspace")].filter(Boolean).join(" \u00b7 ") || "nothing on it yet";
}

export function useTraceCanvases() {
  return useSyncExternalStore(subscribe, listCanvases, listCanvases);
}

export function closeTraceCanvas() {
  if (open?.preview) {
    closeTracePreview();
    return;
  }
  if (!open) return;
  open = null;
  selection = null;
  emit();
}

// A canvas shown in a pane of the workspace home is the one open while the
// reader works in it (`inDeck`): the chat's workspace tools read and lay on
// it, and nothing is laid over the page. Moving from one pane to another
// moves it; a pane let go of lets it go.
export function showTraceCanvasInDeck(id) {
  const rec = id ? records[id] : null;
  if (!rec) return;
  const shown = { id, title: rec.title || "", trace: null, inDeck: true };
  // Under a preview, the pane's canvas waits for it to go.
  if (open?.preview) {
    if (under?.id !== id) {
      under = shown;
      underSelection = null;
    }
    return;
  }
  if (open?.id === id && open.inDeck) return;
  open = shown;
  selection = null;
  emit();
}

export function releaseTraceCanvasInDeck(id) {
  if (open?.preview) {
    if (under?.id === id) {
      under = null;
      underSelection = null;
    }
    return;
  }
  if (!open?.inDeck || open.id !== id) return;
  open = null;
  selection = null;
  emit();
}

// The canvas that is up ({ id, title, trace, focus?, inDeck?, preview? }), or null.
export function traceCanvasOpen() {
  return open;
}

export function useTraceCanvasOpen() {
  return useSyncExternalStore(subscribe, () => open, () => open);
}

const NONE = cleanRecord(null);

// Whether a workspace is kept in this browser (it may hold no session).
export function traceCanvasKept(id) {
  return !!(id && records[id]);
}

// Whether a kept workspace holds anything at all: one emptied (cleared, or
// its last thing taken off) is kept no longer, as on the next load.
export function traceCanvasHolds(id) {
  return !!(id && records[id] && holdsAnything(records[id]));
}

// A canvas no longer kept is no longer open: a preview of it goes (the pane's
// canvas open again), and one waiting under a preview is not put back.
function letGo(id) {
  if (under?.id === id) {
    under = null;
    underSelection = null;
  }
  if (open?.id !== id) return;
  open = open.preview ? under : null;
  selection = open ? underSelection : null;
  under = null;
  underSelection = null;
}

// A workspace the reader deleted, whatever it holds: what it was, for an
// Undo to put back as it was (restoreTraceCanvas). A pane showing it goes
// blank (traceCanvasDeleted), where one let go of for room says it is gone.
const deletedIds = new Set();
export function deleteTraceCanvas(id) {
  const rec = id ? records[id] : null;
  if (!rec) return null;
  const next = { ...records };
  delete next[id];
  letGo(id);
  deletedIds.add(id);
  commit(next);
  return rec;
}
export function restoreTraceCanvas(id, rec) {
  if (!id || !rec || records[id]) return;
  deletedIds.delete(id);
  commit({ ...records, [id]: rec });
}
export const traceCanvasDeleted = (id) => !!id && deletedIds.has(id) && !records[id];

// An emptied workspace let go of at once, rather than on the next load.
// One holding anything stays.
export function forgetEmptyTraceCanvas(id) {
  if (!id || !records[id] || holdsAnything(records[id])) return false;
  const next = { ...records };
  delete next[id];
  letGo(id);
  commit(next);
  return true;
}

export function traceCanvasSnapshot(id) {
  return (id && records[id]) || NONE;
}

export function useTraceCanvas(id) {
  const read = () => (id && records[id]) || NONE;
  return useSyncExternalStore(subscribe, read, read);
}

// A workspace of the reader's own (`pad:`), named for what it was started
// on, holding whatever it was started with: sessions (laid on, none of them
// its anchor), machines, findings, notes, searches. The session's own
// workspace, when one starts on a session, is left as it was.
export function newTraceCanvas({ title = "", sessions = [], machines = [], findings = [], looseNotes = [], searches = [], nested = [] } = {}) {
  const list = (sessions || []).filter((x) => x?.id).map((x) => ({ id: lower(x.id), title: String(x.title || "") }));
  const rec = cleanRecord({
    anchor: "", title: title || list[0]?.title || "", layered: list, machines, findings, nested,
    looseNotes: looseNotes.map((n) => ({ id: thingId("n"), at: Date.now(), ...n })), searches: searches.map((q) => ({ id: thingId("q"), ...q })),
  });
  if (!holdsAnything(rec)) return null;
  const id = thingId("pad:");
  commit(keep(id, rec));
  return id;
}

// Brings machines, findings, notes and searches onto a workspace (its
// sessions come on by layOnTraceCanvas). A machine's card the reader had put
// away comes back with it. A workspace something is put on is the reader's
// (`saved`), as one whose chat is asked is (setTraceCanvasChat).
export function keepOnTraceCanvas(id, { machines = [], findings = [], looseNotes = [], searches = [], nested = [] } = {}) {
  // A workspace goes inside another only where it would not end up inside
  // itself.
  const inside = nested.filter((w) => w?.id && records[w.id] && !reaches(w.id, id));
  update(id, (c) => {
    const ws = cleanNested([...c.nested, ...inside]);
    const ms = cleanMachines([...c.machines, ...machines]);
    const fs = cleanFindings([...c.findings, ...findings]);
    const ns = cleanLooseNotes([...c.looseNotes, ...looseNotes.map((n) => ({ id: thingId("n"), at: Date.now(), ...n }))]);
    const qs = cleanSearches([...c.searches, ...searches.filter((q) => !c.searches.some((x) => x.q === q.q)).map((q) => ({ id: thingId("q"), ...q }))]);
    const back = new Set(machines.map((m) => `graph:machine:${m.id}`));
    const hidden = c.hidden.filter((h) => !back.has(h));
    if (ms.length === c.machines.length && fs.length === c.findings.length && ns.length === c.looseNotes.length && qs.length === c.searches.length && ws.length === c.nested.length && hidden.length === c.hidden.length) return null;
    return { machines: ms, findings: fs, looseNotes: ns, searches: qs, nested: ws, hidden, saved: true };
  });
}

// Whether a workspace is, or holds somewhere inside it, another.
export function reaches(from, target, seen = new Set()) {
  if (from === target) return true;
  if (seen.has(from)) return false;
  seen.add(from);
  return (records[from]?.nested || []).some((w) => reaches(w.id, target, seen));
}

// A note written again (`text`), carried on the board (its place: `x`,
// `y`, `t`, `dx`), or pinned on cells (`links`, all of them).
export function setTraceCanvasNote(id, noteId, patch = {}) {
  setTraceCanvasNotes(id, [{ ...patch, id: noteId }]);
}

// Several notes at once, each { id, text?, x?, y?, t?, dx?, links? }: the board
// moving them all down as one is let go above its top, or placing those that
// have no place yet.
export function setTraceCanvasNotes(id, patches = []) {
  update(id, (c) => {
    const byId = new Map((Array.isArray(patches) ? patches : []).filter((p) => p?.id).map((p) => [p.id, p]));
    let changed = false;
    const looseNotes = c.looseNotes.map((cur) => {
      const p = byId.get(cur.id);
      if (!p) return cur;
      const place = notePlaceOf(p);
      // A place without its moment is one on a board with no clock: the
      // moment it had no longer stands.
      const next = { ...cur, ...(p.text != null ? { text: String(p.text).slice(0, 4000) } : {}), ...place };
      // Its offset is from its moment, so the two go together.
      if (Number.isFinite(place.x) && !Number.isFinite(place.t)) {
        delete next.t;
        delete next.dx;
      }
      if (Number.isFinite(place.t) && !Number.isFinite(place.dx)) delete next.dx;
      if (Array.isArray(p.links)) {
        const links = cleanNoteLinks(p.links);
        if (links.length) next.links = links;
        else delete next.links;
      }
      if (["text", "x", "y", "t", "dx"].every((k) => next[k] === cur[k]) && JSON.stringify(next.links || []) === JSON.stringify(cur.links || [])) return cur;
      changed = true;
      return next;
    });
    return changed ? { looseNotes } : null;
  });
}

const THING_FIELDS = { machine: ["machines", cleanMachines], search: ["searches", cleanSearches], note: ["looseNotes", cleanLooseNotes], workspace: ["nested", cleanNested] };

// Takes a machine, a search or a note off a workspace, saying what it was and
// where it stood among its kind, for an Undo to put it back.
export function takeThingOff(id, kind, thing) {
  const [field] = THING_FIELDS[kind] || [];
  if (!field) return null;
  let was = null;
  update(id, (c) => {
    const at = c[field].findIndex((x) => x.id === thing);
    if (at < 0) return null;
    was = { entry: c[field][at], at };
    return { [field]: c[field].filter((x) => x.id !== thing) };
  });
  return was;
}

// What takeThingOff took, back where it stood.
export function putThingBack(id, kind, { entry, at } = {}) {
  const [field, clean] = THING_FIELDS[kind] || [];
  if (!field || !entry?.id) return;
  update(id, (c) => {
    if (c[field].some((x) => x.id === entry.id)) return null;
    const list = [...c[field]];
    list.splice(Math.max(0, Math.min(Number.isFinite(at) ? at : list.length, list.length)), 0, entry);
    return { [field]: clean(list) };
  });
}

// Lays sessions on beside the anchor (ids, or { id, title }), newest last. A
// session the reader took off and lays on again is no longer left off when
// the canvas follows its chat. A workspace a session is laid on is the
// reader's (`saved`).
export function layOnTraceCanvas(id, entries) {
  update(id, (c) => {
    const layered = freshLayered(c, entries);
    if (!layered) return null;
    const back = new Set(layered.map((t) => t.id));
    return { layered, dismissed: c.dismissed.filter((x) => !back.has(x)), saved: true };
  });
}

// Takes a session laid on beside the anchor off again, for good: following
// the chat leaves it off. The anchor stays: the canvas is anchored on it.
export function takeOffTraceCanvas(id, sid) {
  const off = lower(sid);
  update(id, (c) => {
    if (off === c.anchor || !c.layered.some((t) => t.id === off)) return null;
    const { [off]: _dropped, ...sizes } = c.sizes;
    return {
      layered: c.layered.filter((t) => t.id !== off),
      readings: c.readings.filter((r) => r.session !== off),
      sizes,
      order: c.order.filter((x) => x !== off),
      dismissed: [...c.dismissed.filter((x) => x !== off), off].slice(-MAX_DISMISSED),
    };
  });
}

// A canvas's sessions put back as they were before a change the reader
// undoes from outside the canvas (the fleet's panes): what was laid on, what
// was done to those sessions there, and what following its chat leaves off.
export function restoreTraceCanvasLayers(id, was) {
  if (!was) return;
  update(id, () => ({ layered: was.layered, readings: was.readings, sizes: was.sizes, order: was.order, dismissed: was.dismissed }));
}

// The anchor alone on the board: every other session taken off (and left
// off by following), the machines' cards (`hide`, the board's keys for them:
// the chat's finds and the anchor's related sessions) put away with what the
// reader had already tucked, and what the reader did to the board gone.
export function clearTraceCanvas(id, { hide = [] } = {}) {
  update(id, (c) => {
    const hidden = [...new Set([...c.hidden, ...hide])];
    if (!(c.layered.length || c.readings.length || hidden.length !== c.hidden.length || Object.keys(c.sizes).length || c.order.length || c.machines.length || c.findings.length || c.searches.length || c.nested.length)) return null;
    return {
      layered: [], readings: [], hidden, sizes: {}, order: [], machines: [], findings: [], searches: [], nested: [],
      dismissed: [...new Set([...c.dismissed, ...c.layered.map((t) => t.id)])].slice(-MAX_DISMISSED),
    };
  });
}

// Everything off a workspace, its anchor and the reader's own notes too (the
// deck's Clear): holding nothing, it is let go of where a pane shows it, and
// the pane is blank again (WorkspaceDeck).
export function emptyTraceCanvas(id) {
  update(id, (c) => (holdsAnything(c) ? {
    anchor: "", layered: [], readings: [], sizes: {}, order: [], machines: [], findings: [], looseNotes: [], searches: [], nested: [],
  } : null));
}

// A canvas's name, as its anchor's record now says it: what the lists of
// workspaces call it.
export function setTraceCanvasTitle(id, title) {
  const t = String(title || "");
  update(id, (c) => (!t || c.named || c.title === t ? null : { title: t }));
}

// The reader's own name for a workspace; none gives it back what it holds
// to be called by.
export function renameTraceCanvas(id, title) {
  const t = String(title || "").trim().slice(0, 200);
  // A workspace the reader names is theirs.
  update(id, (c) => (c.title === t && c.named === !!t ? null : { title: t, named: !!t, ...(t ? { saved: true } : {}) }));
}

export function setTraceCanvasReadings(id, readings) {
  update(id, (c) => (c.readings === readings ? null : { readings: Array.isArray(readings) ? readings : [] }));
}

export function setTraceCanvasHidden(id, hidden) {
  update(id, (c) => (c.hidden === hidden ? null : { hidden: Array.isArray(hidden) ? hidden : [] }));
}

export function setTraceCanvasSizes(id, sizes) {
  update(id, (c) => (c.sizes === sizes ? null : { sizes: cleanSizes(sizes) }));
}

export function setTraceCanvasOrder(id, order) {
  update(id, (c) => (c.order === order ? null : { order: cleanCanvasOrder(order) }));
}

// The chat a canvas is worked in (its tab's id): the one last begun in it,
// so opening the canvas opens that chat. A workspace whose chat is asked is
// the reader's (`saved`).
export function setTraceCanvasChat(id, chatId) {
  const value = chatId != null ? String(chatId) : null;
  const cur = id ? records[id] : null;
  if (!cur) return;
  // A chat belongs to one workspace: given to this one, any other lets go.
  const others = value == null ? [] : Object.keys(records).filter((k) => k !== id && records[k].chatId === value);
  if (cur.chatId === value && cur.chatAsked === (value != null) && !others.length) return;
  const next = { ...records, [id]: { ...cur, chatId: value, chatAsked: value != null, saved: cur.saved || value != null } };
  for (const k of others) next[k] = { ...records[k], chatId: null, chatAsked: false };
  commit(next);
}

// Whether a canvas lays on the sessions its chat looks into. Following again
// lays on every session the chat has looked into, the ones taken off
// included, and brings back the machines it found (the board's `graph:`
// keys in the hidden list).
export function setTraceCanvasFollow(id, on) {
  update(id, (c) => (c.follow === !!on ? null : {
    follow: !!on,
    ...(on ? { dismissed: [], hidden: c.hidden.filter((h) => !String(h).startsWith("graph:")) } : {}),
  }));
}

// What is selected on the open canvas, for its chat to be told: a session's
// card, or a cell on it ({ session, title?, lane?, laneLabel?, turn? }).
export function setTraceCanvasSelection(next) {
  const value = next?.session ? next : null;
  if (JSON.stringify(value) === JSON.stringify(selection)) return;
  selection = value;
  emit();
}

export function traceCanvasSelection() {
  return selection;
}

// The traces on the canvas that is up, its anchor (if it has one) first,
// titled as its cards are.
export function openCanvasTraces() {
  return canvasTraces(open?.id);
}

// Every session workspace `id` holds: its own (its anchor first, then those
// laid on) and those of the workspaces inside it, each of those with the
// name of the one it is inside (`inside`, as the workspaces are listed) and
// the workspace `id` holds that it was found through (`via`, named
// `viaTitle`), once each, as far as NESTED_DEPTH workspaces in and at most
// `max` of them.
const NESTED_DEPTH = 3;
export function workspaceSessions(id, { max = 30 } = {}) {
  const out = [];
  const seen = new Set();
  const walked = new Set();
  const nameOf = (wid) => (records[wid] && listedTitle(records[wid])) || "Untitled workspace";
  const visit = (wid, inside, depth, via) => {
    const c = wid ? records[wid] : null;
    if (!c || walked.has(wid) || depth > NESTED_DEPTH) return;
    walked.add(wid);
    for (const [i, t] of [...(c.anchor ? [{ id: c.anchor, title: c.title }] : []), ...c.layered].entries()) {
      const sid = lower(t.id);
      if (!sid || seen.has(sid) || out.length >= max) continue;
      seen.add(sid);
      out.push({ id: sid, title: t.title || "", anchor: depth === 0 && !!c.anchor && i === 0, inside, via, viaTitle: via ? nameOf(via) : null });
    }
    for (const w of c.nested || []) visit(w.id, nameOf(w.id), depth + 1, via || w.id);
  };
  visit(id, null, 0, null);
  return out;
}

// What workspace `id` holds; what its board draws of each while it is the
// board in front. Sessions inside the workspaces it holds come after its
// own, each saying which one it is inside.
export function canvasTraces(id) {
  const c = id ? records[id] : null;
  if (!c) return [];
  const drawn = new Map((id === open?.id ? workspaceCanvasSnapshot()?.cards || [] : []).map((x) => [x.id, x]));
  return workspaceSessions(id).map((t) => ({
    id: t.id, title: drawn.get(t.id)?.title || t.title || "", anchor: t.anchor, card: drawn.get(t.id) || null, inside: t.inside,
  }));
}

// What is about the traces on workspace `id` (findings, notes, the chat's
// claims, sub-agents), as its board lists them while it is the board in
// front: [{ id, kind, session, turn, lane, label, open }], or null while no
// board shows it.
export function canvasThings(id) {
  if (!id || id !== open?.id) return null;
  return workspaceCanvasSnapshot()?.things || null;
}

// What a chat is told about workspace `id` (the canvas that is up, unless
// another is named), one line: what is on it, which trace anchors it, and,
// on the one up, what the reader has selected there.
export function describeTraceCanvas(id = open?.id) {
  const c = id ? records[id] : null;
  if (!c) return null;
  const list = canvasTraces(id);
  const items = list.slice(0, 12).map((t) => `${t.anchor ? "anchor " : ""}session ${t.id}${t.title ? ` ("${t.title.slice(0, 80)}")` : ""}${t.inside ? ` inside the workspace "${t.inside.slice(0, 60)}"` : ""}`);
  const sel = selection && id === open?.id
    ? `; the reader has selected ${selection.lane ? `the ${selection.laneLabel || selection.lane} cell at T${selection.turn} of ` : ""}session ${selection.session}${selection.title ? ` ("${String(selection.title).slice(0, 80)}")` : ""}`
    : "";
  const opened = (canvasThings(id) || []).filter((t) => t.open).length;
  const more = [
    c.searches.length ? `searches on it: ${c.searches.slice(0, 6).map((q) => `"${q.q.slice(0, 80)}"`).join(", ")}` : null,
    c.machines.length ? `machines on it: ${c.machines.slice(0, 6).map((m) => m.name || m.id).join(", ")}` : null,
    c.nested?.length ? `workspaces inside it: ${c.nested.slice(0, 6).map((w) => `"${(records[w.id]?.title || "untitled").slice(0, 60)}"`).join(", ")}` : null,
    c.looseNotes.length ? `the reader's notes on it: ${c.looseNotes.slice(0, 4).map((n) => `"${n.text.replace(/\s+/g, " ").slice(0, 120)}"${n.links?.length ? ` (pinned on ${n.links.slice(0, 4).map((l) => `session ${l.session.slice(0, 8)} T${l.turn}${l.lane ? ` ${l.lane}` : ""}`).join(", ")})` : ""}`).join("; ")}` : null,
    opened ? `${opened} thing${opened === 1 ? "" : "s"} open under its traces` : null,
  ].filter(Boolean).join("; ");
  return `the reader has a workspace of traces open holding ${list.length} session${list.length === 1 ? "" : "s"}${c.anchor ? "" : ", none of them its anchor"}${list.length ? `: ${items.join("; ")}${list.length > 12 ? "; ..." : ""}` : ""}${more ? `; ${more}` : ""}${sel} (workspace_state reads it, workspace_lay_on lays sessions on it and workspace_take_off takes them off, workspace_open opens what is about its traces under them or closes it, workspace_point pins a note on a turn)`;
}

export function describeOpenCanvas() {
  return describeTraceCanvas(open?.id);
}
